import { useEffect, useState } from "react";
import {
  validateRequired,
  validateEmail,
  validatePhone,
  ErrorMessage,
} from "../../utlis/function";
import { callApi } from "../../services/ApiServices";
import { useGlobalStates } from "../../utlis/ClubsContext";
import CommonLoader from "../common/CommonLoader";
import AddressFieldsSection from "./AddressFieldsSection";
import GoogleAddressAutocomplete from "./GoogleAddressAutocomplete";

const fetchAddressFromLatLng = async (lat, lng) => {
  try {
    const res = await fetch(
      `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
    );
    const data = await res.json();

    if (!data.results?.length) return null;

    const result = {
      city: "",
      state: "",
      country: "",
      zipCode: "",
    };

    data.results[0].address_components.forEach((component) => {
      const types = component.types;
      if (types.includes("locality")) result.city = component.long_name;
      if (types.includes("administrative_area_level_1")) {
        result.state = component.long_name;
      }
      if (types.includes("country")) result.country = component.long_name;
      if (types.includes("postal_code")) result.zipCode = component.long_name;
    });

    return result;
  } catch (err) {
    console.error("Reverse geocoding failed", err);
    return null;
  }
};

const emptyAddress = () => ({
  address: "",
  city: "",
  state: "",
  zipCode: "",
  country: "",
  latitude: null,
  longitude: null,
});

const normalizeAddress = (address = {}) => ({
  address: address.address || address.contactAddress || "",
  city: address.city || address.contactCity || "",
  state: address.state || address.contactState || "",
  zipCode: address.zipCode || address.contactZip || "",
  country: address.country || address.contactCountry || "",
  latitude: address.latitude ?? null,
  longitude: address.longitude ?? null,
});

const EditContactInfoModal = ({ isOpen, onClose, initialData }) => {
  const [formData, setFormData] = useState({
    phone: "",
    email: "",
    primaryAddress: emptyAddress(),
    extraAddresses: [],
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { selectedClub, handleSelectedClubDetail } = useGlobalStates();

  const resolveSelectedAddress = async (data) => {
    const {
      address,
      city,
      state,
      country,
      zipCode,
      latitude,
      longitude,
    } = data;

    let finalCity = city;
    let finalState = state;
    let finalCountry = country;
    let finalZip = zipCode;

    if (
      (!finalCity || !finalState || !finalCountry || !finalZip) &&
      latitude &&
      longitude
    ) {
      const extra = await fetchAddressFromLatLng(latitude, longitude);

      if (extra) {
        finalCity = finalCity || extra.city;
        finalState = finalState || extra.state;
        finalZip = finalZip || extra.zipCode;
        finalCountry = finalCountry || extra.country;
      }
    }

    return {
      address,
      city: finalCity,
      state: finalState,
      zipCode: finalZip,
      country: finalCountry,
      latitude,
      longitude,
    };
  };

  const validateZip = (value) => {
    const zip = (value || "").trim();
    if (!validateRequired(zip)) return "Zip code is required";
    if (zip.length < 3) return "Zip code must be at least 3 digits";
    return "";
  };

  useEffect(() => {
    if (!isOpen) return;

    const normalizedAddresses = (initialData?.addresses || []).map(
      normalizeAddress,
    );

    setFormData({
      phone: initialData?.phone || "",
      email: initialData?.email || "",
      primaryAddress: normalizedAddresses[0] || emptyAddress(),
      extraAddresses: normalizedAddresses.slice(1),
    });
    setErrors({});
    setIsSubmitting(false);
  }, [isOpen, initialData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handlePrimaryAddressChange = (patch) => {
    setFormData((prev) => ({
      ...prev,
      primaryAddress: {
        ...prev.primaryAddress,
        ...patch,
      },
    }));
  };

  const handleAddExtraAddress = () => {
    if ((formData.extraAddresses?.length || 0) >= 4) return;

    setFormData((prev) => ({
      ...prev,
      extraAddresses: [...prev.extraAddresses, emptyAddress()],
    }));
  };

  const handleRemoveExtraAddress = (index) => {
    setFormData((prev) => ({
      ...prev,
      extraAddresses: prev.extraAddresses.filter((_, i) => i !== index),
    }));

    setErrors((prev) => ({
      ...prev,
      extraAddresses: (prev.extraAddresses || []).filter((_, i) => i !== index),
    }));
  };

  const handleExtraAddressFieldValidation = (index, field, value) => {
    const error = field === "zipCode" ? validateZip(value) : "";

    setErrors((prev) => {
      const updatedExtraErrors = [...(prev.extraAddresses || [])];
      updatedExtraErrors[index] = {
        ...(updatedExtraErrors[index] || {}),
        [field]: error,
      };

      return {
        ...prev,
        extraAddresses: updatedExtraErrors,
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};

    if (!validateRequired(formData.primaryAddress.address)) {
      newErrors.address = "Address is required";
    }

    if (!validateRequired(formData.phone)) {
      newErrors.phone = "Phone number is required";
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = "Please enter a valid phone number";
    }

    if (!validateRequired(formData.email)) {
      newErrors.email = "Email address is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    // const extraAddressErrors = formData.extraAddresses.map((item) => {
    //   const itemErrors = {};
    //   const hasAnyValue = Object.values(item || {}).some((value) =>
    //     value !== null ? String(value).trim() !== "" : false,
    //   );

    //   if (!hasAnyValue) {
    //     return itemErrors;
    //   }

    //   const zipError = validateZip(item.zipCode || "");
    //   if (zipError) {
    //     itemErrors.zipCode = zipError;
    //   }

    //   return itemErrors;
    // });

    // if (extraAddressErrors.some((item) => Object.keys(item).length > 0)) {
    //   newErrors.extraAddresses = extraAddressErrors;
    // }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }

    const normalizedInitialAddresses = (initialData?.addresses || []).map(
      normalizeAddress,
    );

    const isFormChanged =
      formData.phone !== (initialData?.phone || "") ||
      formData.email !== (initialData?.email || "") ||
      JSON.stringify(formData.primaryAddress) !==
        JSON.stringify(normalizedInitialAddresses[0] || emptyAddress()) ||
      JSON.stringify(formData.extraAddresses) !==
        JSON.stringify(normalizedInitialAddresses.slice(1));

    if (!isFormChanged) {
      onClose();
      return;
    }

    setIsSubmitting(true);

    try {
      const addresses = [
        {
          contactAddress: formData.primaryAddress.address,
          latitude: formData.primaryAddress.latitude,
          longitude: formData.primaryAddress.longitude,
          contactCity: formData.primaryAddress.city,
          contactCountry: formData.primaryAddress.country,
          contactZip: formData.primaryAddress.zipCode,
          contactState: formData.primaryAddress.state,
        },
        ...formData.extraAddresses
          .filter((item) => item.address && item.latitude && item.longitude)
          .map((item) => ({
            contactAddress: item.address,
            latitude: item.latitude,
            longitude: item.longitude,
            contactCity: item.city,
            contactCountry: item.country,
            contactZip: item.zipCode,
            contactState: item.state,
          })),
      ];

      const fields = {
        addresses,
        contactPhone: formData.phone,
        contactEmail: formData.email,
        contactCity: formData.primaryAddress.city,
        contactState: formData.primaryAddress.state,
        contactZip: formData.primaryAddress.zipCode,
        contactCountry: formData.primaryAddress.country,
        latitude: formData.primaryAddress.latitude,
        longitude: formData.primaryAddress.longitude,
      };

      const response = await callApi({
        method: "put",
        url: `club/update/${selectedClub?.id}`,
        data: { fields },
      });

      if (response.status === 200) {
        handleSelectedClubDetail(selectedClub);
        onClose();
      }
    } catch (error) {
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto main-shadow">
        {isSubmitting && <CommonLoader fullScreen />}

        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl md:text-3xl font-medium text-black tracking-[0] leading-[normal]">
            Edit Contact Information
          </h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15 5L5 15M5 5L15 15"
                stroke="#4C4C4C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label
                htmlFor="email"
                className="flex items-center gap-2 text-base font-medium text-black tracking-[0] leading-[normal] mb-2"
              >
                Email <span className="text-red-500 ml-[-6px]">*</span>
              </label>
              <input
                type="text"
                id="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className={`w-full px-4 py-3 bg-white rounded-full text-base font-normal text-black border transition-colors ${
                  errors.email
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-200 focus:border-primary"
                } focus:outline-none`}
                placeholder="Enter email address"
              />
              <ErrorMessage message={errors.email} />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="flex items-center gap-2 text-base font-medium text-black tracking-[0] leading-[normal] mb-2"
              >
                Phone <span className="text-red-500 ml-[-6px]">*</span>
              </label>
              <input
                type="number"
                id="phone"
                value={formData.phone}
                onChange={(e) => handleInputChange("phone", e.target.value)}
                className={`w-full px-4 py-3 bg-white rounded-full text-base font-normal text-black border transition-colors ${
                  errors.phone
                    ? "border-red-500 focus:border-red-500"
                    : "border-gray-200 focus:border-primary"
                } focus:outline-none`}
                placeholder="Enter phone number"
              />
              <ErrorMessage message={errors.phone} />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label
                htmlFor="address"
                className="flex items-center gap-2 text-base font-medium text-black tracking-[0] leading-[normal] mb-2"
              >
                Address <span className="text-red-500 ml-[-6px]">*</span>
              </label>
              <GoogleAddressAutocomplete
                type="updated"
                value={formData.primaryAddress.address}
                onChange={(val) => {
                  handlePrimaryAddressChange({
                    address: val,
                    city: "",
                    country: "",
                    state: "",
                    zipCode: "",
                    latitude: null,
                    longitude: null,
                  });
                }}
                onSelect={async (data) => {
                  const resolvedAddress = await resolveSelectedAddress(data);
                  handlePrimaryAddressChange(resolvedAddress);
                }}
              />
              <ErrorMessage message={errors.address} />
            </div>

            <div>
              <label className="block text-base font-medium text-black tracking-[0] leading-[normal] mb-2">
                City
              </label>
              <input
                value={formData.primaryAddress.city}
                readOnly
                placeholder="Enter city"
                className="w-full px-4 py-3 bg-white rounded-full text-base font-normal text-black border border-gray-200 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-base font-medium text-black tracking-[0] leading-[normal] mb-2">
                State
              </label>
              <input
                value={formData.primaryAddress.state}
                readOnly
                placeholder="Enter state"
                className="w-full px-4 py-3 bg-white rounded-full text-base font-normal text-black border border-gray-200 focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-base font-medium text-black tracking-[0] leading-[normal] mb-2">
                Zip Code
              </label>
              <input
                type="number"
                placeholder="Enter zip code"
                value={formData.primaryAddress.zipCode}
                onChange={(e) => {
                  const zip = e.target.value;
                  if (zip.length > 6) return;
                  handlePrimaryAddressChange({ zipCode: zip });
                }}
                className="w-full px-4 py-3 bg-white rounded-full text-base font-normal text-black border border-gray-200 focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-base font-medium text-black tracking-[0] leading-[normal] mb-2">
                Country
              </label>
              <input
                readOnly
                placeholder="Enter country"
                value={formData.primaryAddress.country}
                className="w-full px-4 py-3 bg-white rounded-full text-base font-normal text-black border border-gray-200 focus:outline-none"
              />
            </div>
          </div>

          <div>
            {formData.extraAddresses.map((item, index) => (
              <AddressFieldsSection
                key={index}
                label={`Address ${index + 2}`}
                type="updated"
                value={item}
                errors={errors.extraAddresses?.[index] || {}}
                editableZip
                showRemove
                onRemove={() => handleRemoveExtraAddress(index)}
                onChange={(val) => {
                  const updated = [...formData.extraAddresses];
                  updated[index] = {
                    ...updated[index],
                    address: val,
                    city: "",
                    state: "",
                    zipCode: "",
                    country: "",
                    latitude: null,
                    longitude: null,
                  };

                  setFormData((prev) => ({
                    ...prev,
                    extraAddresses: updated,
                  }));
                }}
                onZipChange={(val) => {
                  const zip = val.slice(0, 10);
                  const updated = [...formData.extraAddresses];
                  updated[index] = {
                    ...updated[index],
                    zipCode: zip,
                  };

                  setFormData((prev) => ({
                    ...prev,
                    extraAddresses: updated,
                  }));
                  handleExtraAddressFieldValidation(index, "zipCode", zip);
                }}
                onSelect={async (data) => {
                  const resolvedAddress = await resolveSelectedAddress(data);
                  const updated = [...formData.extraAddresses];
                  updated[index] = resolvedAddress;

                  setFormData((prev) => ({
                    ...prev,
                    extraAddresses: updated,
                  }));
                  handleExtraAddressFieldValidation(
                    index,
                    "zipCode",
                    resolvedAddress.zipCode || "",
                  );
                }}
              />
            ))}

            {(formData.extraAddresses.length || 0) < 4 && (
              <div className="flex justify-end mt-3">
                <button
                  type="button"
                  onClick={handleAddExtraAddress}
                  className="text-primary text-sm font-medium hover:underline"
                >
                  + Add another location
                </button>
              </div>
            )}
          </div>

          <div className="flex items-center justify-center gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-6 py-3 bg-transparent border border-primary text-primary font-medium text-base rounded-full max-w-[265px] flex-1 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-3 bg-primary hover:bg-hover disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-medium text-base rounded-full max-w-[265px] flex-1 transition-colors flex items-center justify-center gap-2"
            >
              {isSubmitting ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditContactInfoModal;
